import { ref, watch, nextTick } from 'vue'
import { roundLatLon7, toValidLat, toValidLon } from '@/utils/common'
import { useMapModalStore } from '@/stores/mapModal'
import { DEFAULT_MAP_POSITION } from '@/constants'

// 住所検索結果の上限
const SUGGESTION_LIMIT = 20

type GsiAddressFeature = {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: [number, number] }
  properties: { title: string; addressCode?: string }
}
type Suggestion = { title: string; lat: number; lon: number }

export function useMapModalLogic(props: {
  lat?: number | null
  lon?: number | null
  isOpen: boolean
}) {
  const mapModalStore = useMapModalStore()

  // モーダルの緯度経度
  const modalLat = ref<number | null>(null)
  const modalLon = ref<number | null>(null)

  // 地図表示用の緯度経度
  const mapLat = ref<number | null>(null)
  const mapLon = ref<number | null>(null)

  const gsiMap = ref()

  const addressQuery = ref('')
  const suggestions = ref<Suggestion[]>([])
  const isSearching = ref(false)
  let aborter: AbortController | null = null

  watch(
    () => props.isOpen,
    async (isOpen) => {
      if (!isOpen) return

      // ① 値の初期化
      const lat = toValidLat(props.lat)
      const lon = toValidLon(props.lon)

      modalLat.value = lat ?? DEFAULT_MAP_POSITION.lat
      modalLon.value = lon ?? DEFAULT_MAP_POSITION.lon
      mapLat.value = modalLat.value
      mapLon.value = modalLon.value
      addressQuery.value = ''
      suggestions.value = []

      // ② DOM描画完了を待つ
      await nextTick()

      // ③ Leaflet がサイズを取れるよう 1フレーム待つ
      requestAnimationFrame(() => {
        gsiMap.value?.reloadMap(modalLat.value!, modalLon.value!)
      })
    },
  )

  // 住所検索
  const onSearchAddress = async () => {
    const q = addressQuery.value.trim()
    if (!q) {
      suggestions.value = []
      return
    }
    aborter?.abort()
    aborter = new AbortController()
    isSearching.value = true
    suggestions.value = []
    try {
      const url =
        `https://msearch.gsi.go.jp/address-search/AddressSearch?q=` + encodeURIComponent(q)
      const res = await fetch(url, { signal: aborter.signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      const data = Array.isArray(json) ? (json as GsiAddressFeature[]) : []
      suggestions.value = data.slice(0, SUGGESTION_LIMIT).map((f) => {
        const [lon, lat] = f.geometry.coordinates
        return { title: f.properties.title, lat, lon }
      })
      if (suggestions.value.length === 0) {
        addressQuery.value = ''
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') return
      suggestions.value = []
    } finally {
      isSearching.value = false
    }
  }

  // 住所候補選択
  const selectSuggestion = (s: Suggestion) => {
    const lat = roundLatLon7(s.lat)
    const lon = roundLatLon7(s.lon)
    mapLat.value = lat
    mapLon.value = lon
    modalLat.value = lat
    modalLon.value = lon
    addressQuery.value = s.title
    suggestions.value = []
    isSearching.value = false
  }

  // 地図を更新
  const applyToMap = () => {
    const lat = toValidLat(modalLat.value)
    const lon = toValidLon(modalLon.value)
    if (lat == null || lon == null) return

    mapLat.value = lat
    mapLon.value = lon
  }

  return {
    mapModalStore,
    modalLat,
    modalLon,
    mapLat,
    mapLon,
    gsiMap,
    addressQuery,
    suggestions,
    isSearching,
    onSearchAddress,
    selectSuggestion,
    applyToMap,
  }
}
